import ErrorAlert from "@/utility/ErrorAlert";
import http from "@/axios/http-common";
import {
  RetensiSession,
  RetensiBatch,
  RetensiDokumen,
  RetensiChecklistItem,
  ScanKandidatParams,
  SignPayload,
  BeritaAcaraJenis,
} from "@/types/RetensiAlihMedia";

// Routes mirror rme/routes/api.php → prefix('retensi-alih-media')
const BASE = "/retensi-alih-media";

function handleError(err: any) {
  ErrorAlert.showErrorAlert(err);
  return Promise.reject(err);
}

// ===== SESSIONS =====
async function listSessions(params: any = {}) {
  return await http.getDataParam(`${BASE}/sessions`, { params }).catch(handleError);
}

async function showSession(id: number) {
  return await http.getData(`${BASE}/sessions/${id}`).catch(handleError);
}

async function createSession(data: Partial<RetensiSession>) {
  return await http.postData(`${BASE}/sessions`, data).catch(handleError);
}

async function updateSession(id: number, data: Partial<RetensiSession>) {
  return await http.putData(`${BASE}/sessions/${id}`, data).catch(handleError);
}

async function scanKandidat(payload: ScanKandidatParams) {
  return await http.postData(`${BASE}/sessions/scan-kandidat`, payload).catch(handleError);
}

async function finalize(id: number) {
  return await http.postData(`${BASE}/sessions/${id}/finalize`, {}).catch(handleError);
}

// ===== BATCHES =====
async function createBatch(sessionId: number, data: Partial<RetensiBatch>) {
  return await http.postData(`${BASE}/sessions/${sessionId}/batches`, data).catch(handleError);
}

async function showBatch(id: number) {
  return await http.getData(`${BASE}/batches/${id}`).catch(handleError);
}

async function updateBatch(id: number, data: Partial<RetensiBatch>) {
  return await http.putData(`${BASE}/batches/${id}`, data).catch(handleError);
}

// ===== DOKUMEN =====
async function uploadDokumen(batchId: number, formData: FormData) {
  return await http
    .postData(`${BASE}/batches/${batchId}/dokumen`, formData)
    .catch(handleError);
}

async function showDokumen(id: number) {
  return await http.getData(`${BASE}/dokumen/${id}`).catch(handleError);
}

async function verifyDokumen(
  id: number,
  data: {
    halaman_terverifikasi: number;
    pmik_verifikator_id: number;
    pmik_verifikator_nama: string;
    watermark_applied?: boolean;
  }
) {
  return await http.putData(`${BASE}/dokumen/${id}/verify`, data).catch(handleError);
}

async function deleteDokumen(id: number) {
  return await http.deleteData(`${BASE}/dokumen/${id}`).catch(handleError);
}

// ===== DAFTAR BERKAS (blanko URM) =====
async function listBerkas(batchId: number) {
  return await http.getData(`${BASE}/batches/${batchId}/berkas`).catch(handleError);
}

async function scanBerkas(
  batchId: number,
  params: { tahun_terlama?: number; tahun_terbaru?: number; unit?: string; limit?: number } = {}
) {
  return await http
    .postData(`${BASE}/batches/${batchId}/berkas/scan`, params)
    .catch(handleError);
}

async function tambahBerkas(batchId: number, registerNo: number[]) {
  return await http
    .postData(`${BASE}/batches/${batchId}/berkas`, { register_no: registerNo })
    .catch(handleError);
}

async function updateBerkas(id: number, keterangan: string | null) {
  return await http.putData(`${BASE}/berkas/${id}`, { keterangan }).catch(handleError);
}

async function deleteBerkas(id: number) {
  return await http.deleteData(`${BASE}/berkas/${id}`).catch(handleError);
}

// ===== CHECKLIST =====
async function bulkSaveChecklist(
  sessionId: number,
  items: Partial<RetensiChecklistItem>[]
) {
  return await http
    .putData(`${BASE}/sessions/${sessionId}/checklist/bulk`, { items })
    .catch(handleError);
}

// ===== SIGNATURES =====
async function listSignatures(sessionId: number) {
  return await http.getData(`${BASE}/sessions/${sessionId}/signatures`).catch(handleError);
}

async function sign(sessionId: number, payload: SignPayload) {
  return await http.postData(`${BASE}/sessions/${sessionId}/sign`, payload).catch(handleError);
}

// ===== BERITA ACARA =====
async function listBeritaAcara(sessionId: number) {
  return await http
    .getData(`${BASE}/sessions/${sessionId}/berita-acara`)
    .catch(handleError);
}

async function showBeritaAcara(sessionId: number, jenis: BeritaAcaraJenis) {
  return await http
    .getData(`${BASE}/sessions/${sessionId}/berita-acara/${jenis}`)
    .catch(handleError);
}

async function createBeritaAcara(
  sessionId: number,
  data: {
    jenis: BeritaAcaraJenis;
    tgl_ba?: string;
    metode_pemusnahan?: string;
    ringkasan?: Record<string, any>;
  }
) {
  return await http
    .postData(`${BASE}/sessions/${sessionId}/berita-acara`, data)
    .catch(handleError);
}

// ===== PDF / EXCEL URLs (untuk window.open dari komponen) =====
function laporanPdfUrl(sessionId: number): string {
  const base =
    (process.env.VUE_APP_API_URL as string) || "http://localhost:8001/api/v1";
  return `${base}${BASE}/sessions/${sessionId}/pdf`;
}

function excelUrl(sessionId: number): string {
  const base =
    (process.env.VUE_APP_API_URL as string) || "http://localhost:8001/api/v1";
  return `${base}${BASE}/sessions/${sessionId}/excel`;
}

function daftarBerkasPdfUrl(batchId: number): string {
  const base =
    (process.env.VUE_APP_API_URL as string) || "http://localhost:8001/api/v1";
  return `${base}${BASE}/batches/${batchId}/berkas/pdf`;
}

function beritaAcaraPdfUrl(sessionId: number, jenis: BeritaAcaraJenis): string {
  const base =
    (process.env.VUE_APP_API_URL as string) || "http://localhost:8001/api/v1";
  return `${base}${BASE}/sessions/${sessionId}/berita-acara/${jenis}/pdf`;
}

export default {
  // sessions
  listSessions,
  showSession,
  createSession,
  updateSession,
  scanKandidat,
  finalize,
  // batches
  createBatch,
  showBatch,
  updateBatch,
  // dokumen
  uploadDokumen,
  showDokumen,
  verifyDokumen,
  deleteDokumen,
  // daftar berkas
  listBerkas,
  scanBerkas,
  tambahBerkas,
  updateBerkas,
  deleteBerkas,
  // checklist
  bulkSaveChecklist,
  // signatures
  listSignatures,
  sign,
  // berita acara
  listBeritaAcara,
  showBeritaAcara,
  createBeritaAcara,
  // url helpers
  laporanPdfUrl,
  excelUrl,
  daftarBerkasPdfUrl,
  beritaAcaraPdfUrl,
};
